import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import React from "react";

import LoadingButton from "../../../../../../design/wilya-loading-button";
import { WorkerMovementEligibility, WorkerMovementType } from "../../../../store/gat-admin-slice-types";

interface WorkerMovementEditDialogProps {
	open: boolean;
	onClose: () => void;
	scope: "samePatternMovement" | "crossPatternMovement";
	initialValues: WorkerMovementEligibility;
	workerTypes: any[];
	workerSubTypes: any[];
	onSave: (values: WorkerMovementEligibility) => void;
	isSaving: boolean;
}

interface SelectOption {
	id: string;
	label: string;
	isWorkerType: boolean;
	workerTypeName: string;
	workerTypeId: number;
	subTypeName?: string;
}

const WorkerMovementEditDialog: React.FC<WorkerMovementEditDialogProps> = ({
	open,
	onClose,
	scope,
	initialValues,
	workerTypes,
	workerSubTypes,
	onSave,
	isSaving,
}) => {
	const scopeTitle = scope === "samePatternMovement" ? "Job/Department Movement" : "Shift Movement";

	// Build flat list: Worker Types first, then their subtypes (labeled as "WorkerType - SubType")
	const buildOptions = (): SelectOption[] => {
		const options: SelectOption[] = [];

		if (!workerTypes || workerTypes.length === 0) {
			return options;
		}

		workerTypes.forEach((wt) => {
			// Add main worker type
			options.push({
				id: wt.name,
				label: wt.text,
				isWorkerType: true,
				workerTypeName: wt.name,
				workerTypeId: wt.id,
			});

			// Add subtypes labeled as "WorkerType - SubType" only if workerSubTypes exist
			if (workerSubTypes && workerSubTypes.length > 0) {
				const subtypesForType = workerSubTypes.filter((st) => st.workerTypeId === wt.id);
				subtypesForType.forEach((st) => {
					options.push({
						id: `${wt.name}_${st.name}`,
						label: `${wt.text} - ${st.name}`,
						isWorkerType: false,
						workerTypeName: wt.name,
						workerTypeId: wt.id,
						subTypeName: st.name,
					});
				});
			}
		});

		return options;
	};

	const options = buildOptions();

	// Transform initial values to selected options
	const getInitialSelectedOptions = (): SelectOption[] => {
		const movements = initialValues[scope] || [];
		const selected: SelectOption[] = [];

		movements.forEach((movement) => {
			if (movement.allSubTypesAllowed) {
				// Select main worker type
				const workerTypeOption = options.find(
					(opt) => opt.id === movement.workerType && opt.isWorkerType
				);
				if (workerTypeOption) {
					selected.push(workerTypeOption);
				}
			} else {
				// Select specific subtypes
				movement.subTypes.forEach((subTypeName) => {
					const subTypeOption = options.find(
						(opt) =>
							opt.id === `${movement.workerType}_${subTypeName}` &&
							!opt.isWorkerType &&
							opt.subTypeName === subTypeName
					);
					if (subTypeOption) {
						selected.push(subTypeOption);
					}
				});
			}
		});

		return selected;
	};

	const formInitialValues = {
		selectedOptions: getInitialSelectedOptions(),
	};

	// Transform selected options back to API format
	const transformToApiFormat = (selectedOptions: SelectOption[]): WorkerMovementType[] => {
		const workerTypeMap = new Map<string, { allSubTypes: boolean; specificSubTypes: string[] }>();

		selectedOptions.forEach((option) => {
			const workerTypeName = option.workerTypeName;

			if (!workerTypeMap.has(workerTypeName)) {
				workerTypeMap.set(workerTypeName, { allSubTypes: false, specificSubTypes: [] });
			}

			const entry = workerTypeMap.get(workerTypeName)!;

			if (option.isWorkerType) {
				// Main worker type selected = all subtypes
				entry.allSubTypes = true;
			} else {
				// Specific subtype selected
				if (option.subTypeName) {
					entry.specificSubTypes.push(option.subTypeName);
				}
			}
		});

		const result: WorkerMovementType[] = [];
		workerTypeMap.forEach((entry, workerTypeName) => {
			result.push({
				workerType: workerTypeName,
				subTypes: entry.allSubTypes ? [] : entry.specificSubTypes,
				allSubTypesAllowed: entry.allSubTypes,
			});
		});

		return result;
	};

	const handleSubmit = (values: any) => {
		const updatedMovements = transformToApiFormat(values.selectedOptions);

		const payload: WorkerMovementEligibility = {
			...initialValues,
			[scope]: updatedMovements,
		};

		onSave(payload);
	};

	// Check if a worker type is selected (determines if subtypes should be disabled)
	const isWorkerTypeSelected = (workerTypeName: string, selectedOptions: SelectOption[]) => {
		return selectedOptions.some((opt) => opt.isWorkerType && opt.workerTypeName === workerTypeName);
	};

	// Check if option should appear selected (either actually selected or parent is selected)
	const isOptionVisuallySelected = (option: SelectOption, selectedOptions: SelectOption[]) => {
		// Actually selected
		if (selectedOptions.some((opt) => opt.id === option.id)) {
			return true;
		}

		// Subtype whose parent worker type is selected
		if (!option.isWorkerType && isWorkerTypeSelected(option.workerTypeName, selectedOptions)) {
			return true;
		}

		return false;
	};

	// Check if option should be disabled (subtypes when parent is selected)
	const isOptionDisabled = (option: SelectOption, selectedOptions: SelectOption[]) => {
		if (!option.isWorkerType && isWorkerTypeSelected(option.workerTypeName, selectedOptions)) {
			return true;
		}
		return false;
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth={false}
			sx={{
				"& .MuiDialog-paper": {
					width: "700px",
					maxWidth: "700px",
				},
			}}
		>
			<DialogTitle sx={{ padding: "45px 45px 24px" }}>Scope of {scopeTitle}</DialogTitle>
			<Formik initialValues={formInitialValues} onSubmit={handleSubmit} enableReinitialize>
				{({ values, setFieldValue, dirty }) => (
					<Form>
						<DialogContent sx={{ padding: "0px 45px 24px" }}>
							<Box sx={{mt: 1}}>
								
							<Autocomplete
								multiple
								options={options}
								value={values.selectedOptions}
								onChange={(event, newValue, reason, details) => {
									// If adding a worker type, remove its specific subtypes from selection
									if (reason === "selectOption" && details?.option?.isWorkerType) {
										const workerTypeName = details.option.workerTypeName;
										const filtered = newValue.filter(
											(opt) =>
												!(
													!opt.isWorkerType &&
													opt.workerTypeName === workerTypeName
												)
										);
										setFieldValue("selectedOptions", filtered);
									}
									// If removing a worker type, just update normally
									else if (reason === "removeOption" && details?.option?.isWorkerType) {
										setFieldValue("selectedOptions", newValue);
									}
									// If adding a subtype, remove the parent worker type if it exists
									else if (reason === "selectOption" && details?.option && !details.option.isWorkerType) {
										const workerTypeName = details.option.workerTypeName;
										const filtered = newValue.filter(
											(opt) =>
												!(
													opt.isWorkerType &&
													opt.workerTypeName === workerTypeName
												)
										);
										setFieldValue("selectedOptions", filtered);
									}
									// Default behavior for other cases
									else {
										setFieldValue("selectedOptions", newValue);
									}
								}}
								getOptionLabel={(option) => option.label}
								isOptionEqualToValue={(option, value) => option.id === value.id}
								getOptionDisabled={(option) => isOptionDisabled(option, values.selectedOptions)}
								renderOption={(props, option) => {
									const visuallySelected = isOptionVisuallySelected(option, values.selectedOptions);
									const disabled = isOptionDisabled(option, values.selectedOptions);
									const { key, ...otherProps } = props as any;

									return (
										<li key={key} {...otherProps} style={{ opacity: disabled ? 0.6 : 1, paddingLeft: option.isWorkerType ? "16px" : "32px" }}>
											<Checkbox
												icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
												checkedIcon={<CheckBoxIcon fontSize="small" />}
												style={{ marginRight: 8 }}
												checked={visuallySelected}
												disabled={disabled}
											/>
											<Typography
												sx={{
													fontSize: "14px",
													fontWeight: option.isWorkerType ? 600 : 400,
													color: disabled
														? "rgba(0, 0, 0, 0.38)"
														: "rgba(0, 0, 0, 0.87)",
												}}
											>
												{option.label}
											</Typography>
										</li>
									);
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										variant="outlined"
										label="Worker Types & Subtypes"
										placeholder="Select worker types or specific subtypes"
										helperText="Selecting a worker type automatically includes all its subtypes (current and future)"
										sx={{
											"& .MuiInputLabel-root": {
												marginBottom: "8px",
											},
										}}
									/>
								)}
								disableCloseOnSelect
							/>
							</Box>

						</DialogContent>
						<DialogActions sx={{ padding: "33px 45px" }}>
							<Button onClick={onClose} color="primary">
								CANCEL
							</Button>
							<LoadingButton
								type="submit"
								variant="contained"
								color="primary"
								sx={{ backgroundColor: "#2E4374" }}
								disabled={!dirty}
								loading={isSaving}
							>
								SAVE
							</LoadingButton>
						</DialogActions>
					</Form>
				)}
			</Formik>
		</Dialog>
	);
};

export default WorkerMovementEditDialog;
